import type { FC } from 'react'
import type { SxProps, Theme } from '@mui/material'
import type { SystemStyleObject } from '@mui/system'
import { Box, Collapse } from '@mui/material'
import type { SessionNodeProps } from './types/SessionTreeProps'
import {
  createNodeHeaderSx,
  createToggleSx,
  createNodeIconSx,
  createWrapperSx,
  labelSx,
  actionsSx,
  createContentContainerSx,
  DEFAULT_EXPANDED_ICON,
  DEFAULT_COLLAPSED_ICON,
  DEFAULT_SESSION_FONT_SIZE,
  DEFAULT_SESSION_FONT_WEIGHT,
  DEFAULT_VERTICAL_GAP,
  DEFAULT_TOGGLE_WIDTH,
  nodeRowHoverRevealSx,
} from './styles/sessionTreeStyles'

export const SessionNode: FC<SessionNodeProps> = ({
  label,
  icon,
  styleConfig,
  actions,
  persistentActions,
  isExpanded,
  onToggle,
  expandPosition,
  contentContainerConfig,
  children,
}) => {
  const toggleIcon = isExpanded
    ? (styleConfig?.toggleIcon?.expanded ?? DEFAULT_EXPANDED_ICON)
    : (styleConfig?.toggleIcon?.collapsed ?? DEFAULT_COLLAPSED_ICON)

  const verticalGap = styleConfig?.verticalGap ?? DEFAULT_VERTICAL_GAP
  const wrapperSx = createWrapperSx(styleConfig?.wrapper)

  const nodeContent = (
    <>
      <Box sx={nodeRowSx}>
        {!wrapperSx && <Box sx={level2IndentSpacerSx} />}
        <Box
          sx={createNodeHeaderSx(
            styleConfig,
            DEFAULT_SESSION_FONT_SIZE,
            DEFAULT_SESSION_FONT_WEIGHT,
          )}
          onClick={onToggle}
        >
          {expandPosition === 'left' && (
            <Box className="node-toggle" sx={createToggleSx(styleConfig)}>
              {toggleIcon}
            </Box>
          )}

          {icon !== null && icon !== undefined && (
            <Box
              sx={[
                createNodeIconSx(styleConfig) as SystemStyleObject<Theme>,
                { marginLeft: `${SESSION_ICON_ALIGNMENT_OFFSET_PX}px` },
              ]}
            >
              {icon}
            </Box>
          )}

          <Box sx={labelSx}>{label}</Box>

          {((actions && actions.length > 0) ||
            (persistentActions && persistentActions.length > 0)) && (
            <Box sx={rightGroupSx}>
              {actions && actions.length > 0 && (
                <Box
                  className="actions-container"
                  sx={[actionsSx as SystemStyleObject<Theme>, { marginLeft: 0 }]}
                >
                  {actions}
                </Box>
              )}

              {persistentActions && persistentActions.length > 0 && (
                <Box sx={persistentActionsSx}>{persistentActions}</Box>
              )}
            </Box>
          )}

          {expandPosition === 'right' && (
            <Box className="node-toggle" sx={createToggleSx(styleConfig)}>
              {toggleIcon}
            </Box>
          )}
        </Box>
      </Box>

      <Collapse in={isExpanded}>
        <Box sx={createContentContainerSx(contentContainerConfig)}>{children}</Box>
      </Collapse>
    </>
  )

  if (wrapperSx) {
    return <Box sx={createNodeContainerWithWrapperSx(wrapperSx, verticalGap)}>{nodeContent}</Box>
  }

  return <Box sx={createNodeContainerSx(verticalGap)}>{nodeContent}</Box>
}

// Nudges the SESSION agent-mode diamond right so its center lines up with the
// leading-icon column of the card content (event icon + activity dot) in the
// idle, non-hover state. Session nodes carry a wrapper, so no level-2 indent
// spacer is rendered; the wrapper padding is a common offset on both the header
// row and the content container and therefore cancels out of this delta.
//
//   diamond center  = toggle(24) + headerGap(8) + nodeIconBox/2(12)            = 44px
//   card icon center = contentMarginLeft(32) + border(1) + contentPadding(12)
//                      + leadingSlot/2(10)                                      = 55px
//   delta            = 55 - 44                                                  = 11px
//
// Header gap = MUI gap:1 (8px); toggle/nodeIcon widths = 1.5rem (24px);
// content marginLeft = calc(1.5rem + 8px) (32px), padding = 1.5 (12px), with a
// 1px border under box-sizing:border-box. Touches ONLY the session diamond —
// host/project nodes render their own components and stay unchanged.
const SESSION_ICON_ALIGNMENT_OFFSET_PX = 11

const nodeRowSx: SxProps<Theme> = [{ display: 'flex', alignItems: 'center' }, nodeRowHoverRevealSx]

// Owns the auto-margin that used to live on the actions box, so the hover-gated
// actions and the always-visible persistent box share a single right-aligned
// group. The gap here is slightly wider than the inter-action gap to visually
// separate the always-visible draft icon from the hover-revealed controls.
const rightGroupSx: SxProps<Theme> = {
  display: 'flex',
  alignItems: 'center',
  gap: 1,
  marginLeft: 'auto',
}

// Sibling of .actions-container — intentionally carries no opacity/visibility so
// it stays visible regardless of the parent row's hover state.
const persistentActionsSx: SxProps<Theme> = {
  display: 'flex',
  alignItems: 'center',
  gap: 0.5,
}

const level2IndentSpacerSx: SxProps<Theme> = {
  width: `calc(${DEFAULT_TOGGLE_WIDTH} * 2)`,
  minWidth: `calc(${DEFAULT_TOGGLE_WIDTH} * 2)`,
}

function createNodeContainerSx(verticalGap: number): SxProps<Theme> {
  return {
    display: 'flex',
    flexDirection: 'column',
    gap: verticalGap,
    mb: verticalGap,
  }
}

function createNodeContainerWithWrapperSx(
  wrapperSx: SxProps<Theme>,
  verticalGap: number,
): SxProps<Theme> {
  return {
    ...(wrapperSx as object),
    display: 'flex',
    flexDirection: 'column',
    gap: verticalGap,
  }
}
